import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth-context';
import { Button, Input, Label, Card, CardContent, CardHeader, CardTitle } from '@erp/ui';

// Lands here after the user clicks the recovery link in the password
// reset email. By the time this component mounts, Supabase JS has
// already detected the recovery token in the URL hash, exchanged it
// for a session, and emitted PASSWORD_RECOVERY (which the auth context
// captures into `isPasswordRecovery`). All this page does is show a
// new-password form and call `updatePassword` once the user submits.
export function UpdatePasswordPage() {
  const { t } = useTranslation();
  const { updatePassword, user, isPasswordRecovery, isLoading } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError(t('auth.passwordTooShort', 'Le mot de passe doit faire au moins 6 caractères.'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.passwordMismatch', 'Les deux mots de passe ne correspondent pas.'));
      return;
    }
    setSubmitting(true);
    try {
      await updatePassword(password);
      navigate('/', { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Block the page if there's no recovery session AND no logged-in
  // user — landing here without auth means a stale or invalid link.
  // We don't bounce immediately on first paint because auth state can
  // still be loading.
  if (!isLoading && !user && !isPasswordRecovery) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted px-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">
              {t('auth.linkExpired', 'Lien expiré ou invalide')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              {t(
                'auth.requestNewLink',
                "Le lien de réinitialisation est expiré ou n'est plus valide. Demandez un nouveau lien.",
              )}
            </p>
            <Button className="w-full" onClick={() => navigate('/forgot-password')}>
              {t('auth.forgotTitle', 'Mot de passe oublié')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            {t('auth.updatePasswordTitle', 'Nouveau mot de passe')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t(
                'auth.updatePasswordSubtitle',
                'Choisissez votre nouveau mot de passe.',
              )}
            </p>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.newPassword', 'Nouveau mot de passe')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">{t('auth.confirmPassword', 'Confirmer')}</Label>
              <Input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                minLength={6}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting
                ? t('common.loading')
                : t('auth.updatePasswordSubmit', 'Enregistrer le nouveau mot de passe')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
